import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../../../services/api';

const STATUS_COLORS = {
  Pending: 'bg-yellow-100 text-yellow-800',
  Approved: 'bg-green-100 text-green-800',
  Rejected: 'bg-red-100 text-red-800',
};

const DeletionRequests = () => {
  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(false);

  // Confirm modal state
  const [modal, setModal] = useState({ open: false, action: null, request: null });
  const [adminNotes, setAdminNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (statusFilter) params.set('status', statusFilter);
      const res = await api.get(`/account/admin/deletion-requests?${params}`);
      if (res.success) {
        setRequests(res.data.requests);
        setTotal(res.data.total);
        setTotalPages(res.data.totalPages);
      }
    } catch {
      toast.error('Failed to load deletion requests');
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openModal = (action, request) => {
    setAdminNotes('');
    setModal({ open: true, action, request });
  };

  const closeModal = () => setModal({ open: false, action: null, request: null });

  const handleConfirm = async () => {
    const { action, request } = modal;
    setActionLoading(true);
    try {
      const endpoint = `/account/admin/deletion-requests/${request.id}/${action}`;
      const res = await api.post(endpoint, { admin_notes: adminNotes });
      if (res.success) {
        toast.success(action === 'approve' ? 'Request approved — user deleted.' : 'Request rejected.');
        closeModal();
        load();
      } else {
        toast.error(res.message || 'Action failed');
      }
    } catch (err) {
      toast.error(err?.data?.message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const fmt = (d) => d ? new Date(d).toLocaleString() : '—';

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-gray-800 m-0">Account Deletion Requests</h1>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#ffd200]"
        >
          <option value="">All Statuses</option>
          <option value="Pending">Pending</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
        </select>
      </div>

      <div className="bg-white rounded shadow-sm">
        {/* Top controls */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Display</span>
            <select
              value={limit}
              onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#ffd200]"
            >
              {[10, 25, 50].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span className="text-sm text-gray-600">Records</span>
          </div>

          {/* Pagination */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {[...Array(Math.min(totalPages, 9))].map((_, i) => {
              const n = i + 1;
              return (
                <button
                  key={n}
                  onClick={() => setPage(n)}
                  className={`px-3 py-1.5 text-sm border border-gray-300 ${page === n ? 'bg-[#ffd200] text-black border-[#ffd200]' : 'bg-white hover:bg-gray-50'}`}
                >
                  {n}
                </button>
              );
            })}
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || loading}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-100 border-b border-gray-300">
              <tr>
                {['#', 'User ID', 'Name', 'Email', 'User Type', 'Reason', 'Requested At', 'Status', 'Reviewed By', 'Notes', 'Action'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-700 border-r border-gray-300 last:border-r-0 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan={11} className="px-4 py-8 text-center text-gray-500"><i className="fas fa-spinner fa-spin mr-2" />Loading...</td></tr>
              ) : requests.length === 0 ? (
                <tr><td colSpan={11} className="px-4 py-8 text-center text-gray-500">No deletion requests found</td></tr>
              ) : requests.map((r, i) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">{(page - 1) * limit + i + 1}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">{r.user?.user_id || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200 whitespace-nowrap">
                    {r.user ? `${r.user.first_name} ${r.user.last_name}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">{r.user?.email || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200">{r.user?.user_type || '—'}</td>
                  <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200 max-w-[160px] truncate" title={r.reason || ''}>
                    {r.reason || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200 whitespace-nowrap">{fmt(r.requested_at)}</td>
                  <td className="px-4 py-3 text-sm border-r border-gray-200">
                    <span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded ${STATUS_COLORS[r.status] || 'bg-gray-100 text-gray-700'}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200 whitespace-nowrap">
                    {r.reviewer ? `${r.reviewer.first_name} ${r.reviewer.last_name}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 border-r border-gray-200 max-w-[160px] truncate" title={r.admin_notes || ''}>
                    {r.admin_notes || '—'}
                  </td>
                  <td className="px-4 py-3">
                    {r.status === 'Pending' ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => openModal('approve', r)}
                          className="h-6 px-2 text-xs font-semibold rounded bg-green-600 text-white hover:bg-green-700 transition-colors"
                          title="Approve"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => openModal('reject', r)}
                          className="h-6 px-2 text-xs font-semibold rounded bg-red-500 text-white hover:bg-red-600 transition-colors"
                          title="Reject"
                        >
                          Reject
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 italic">{r.status}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 border-t border-gray-200 text-sm text-gray-600">
          Showing {requests.length > 0 ? (page - 1) * limit + 1 : 0} to {Math.min(page * limit, total)} of {total} entries
        </div>
      </div>

      {/* Approve / Reject modal */}
      {modal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-1">
              {modal.action === 'approve' ? 'Approve Account Deletion' : 'Reject Account Deletion'}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {modal.action === 'approve'
                ? `Approve deletion for ${modal.request?.user?.first_name} ${modal.request?.user?.last_name}? Their account will be permanently deleted from the database.`
                : `Reject deletion for ${modal.request?.user?.first_name} ${modal.request?.user?.last_name}? Their account will remain active.`}
            </p>
            {modal.request?.reason && (
              <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
                <p className="text-xs font-semibold text-gray-700 mb-1">User's Reason:</p>
                <p className="text-sm text-gray-600">{modal.request.reason}</p>
              </div>
            )}
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Admin Notes <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              rows={3}
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="Add a note for the user..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#ffd200] resize-none mb-5"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={closeModal}
                disabled={actionLoading}
                className="px-4 py-2 text-sm font-medium border border-gray-300 rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={actionLoading}
                className={`px-4 py-2 text-sm font-semibold rounded text-white transition-colors disabled:opacity-50 ${modal.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-500 hover:bg-red-600'
                  }`}
              >
                {actionLoading ? 'Processing...' : modal.action === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeletionRequests;
